import { Metric, Metrics, TypeAttribute, TypeMetricKey } from '@trace/types';
import { useContext, useEffect, useState } from 'react';
import { useQuery, UseQueryResult } from 'react-query';

import { ApiContext } from '../Api/Api';
import { URLStateContext } from '../URLState/URLState';
import {
  METRIC_BY_KEY,
  METRIC_FILTERS_BY_KEY,
  METRIC_IS_COUNTER_BY_KEY,
} from '../../constants/Metrics';
import appendURLSearchParams from '../../utils/appendURLSearchParams';
import getFormattedFilters from '../../utils/getFormattedFilters';
import getJoinedNullDataSet from '../../utils/metric/getJoinedNullDataSet';
import getStepFromInterval from '../../utils/time/getStepFromTimeRange';
import getDateRange from '../../utils/time/getTimeRange';

interface HookPayload {
  isEmpty: boolean;
  isFetched: boolean;
  metrics: Record<string, Metric<'percentile'>>;
}

export type HookResult = Pick<UseQueryResult, 'isError' | 'isFetching' | 'refetch'> & HookPayload;

export default function usePercentileMetric(
  metric: TypeMetricKey,
  groupBy?: TypeAttribute,
): HookResult {
  const { get } = useContext(ApiContext);
  const {
    attributeGroups,
    attributeSort,
    dateFrom,
    dateTo,
    demo,
    duration,
    filters,
    session,
    step,
  } = {
    ...useContext(URLStateContext),
  };

  const attribute = groupBy || attributeGroups[metric];
  const { dateFrom: start, dateTo: end } = getDateRange(duration, dateFrom, dateTo);
  const [sortBy, sortOrder] = attributeSort.split(':');
  const [payload, setPayload] = useState<Omit<HookPayload, 'isFetched'>>({
    isEmpty: true,
    metrics: {},
  });

  const { data, isError, isFetching, isSuccess, refetch } = useQuery<Metrics<'percentile'>>(
    [
      'percentileMetric',
      { attribute, demo, end, filters, metric, session, start, step, sortBy, sortOrder },
    ],
    async () =>
      get(
        `/metrics/${METRIC_BY_KEY[metric]}?${appendURLSearchParams(
          '',
          ...METRIC_FILTERS_BY_KEY[metric],
          ['filter', getFormattedFilters(filters)],
          ['step', step ? step.toString() : getStepFromInterval(start, end).toString()],
          ['start', start.toString()],
          ['end', end.toString()],
          ['group_by', attribute],
          ['session', session],
          ['sort_by', sortBy],
          ['sort_order', sortOrder],
          ['views', 'percentile'],
        )}`,
      ),
  );

  /**
   * Use a side-effect to:
   *  - Update the `payload` state whenever the percentileMetric query resolves
   */
  useEffect(() => {
    if (data) {
      Object.keys(data).forEach((key) => {
        data[key].name = metric;

        if (METRIC_IS_COUNTER_BY_KEY[metric]) {
          data[key].views.percentile.p5 = getJoinedNullDataSet(data[key].views.percentile.p5, {
            fill: 'zero',
            withEnd: true,
            withStart: true,
          });

          data[key].views.percentile.p50 = getJoinedNullDataSet(data[key].views.percentile.p50, {
            fill: 'zero',
            withEnd: true,
            withStart: true,
          });

          data[key].views.percentile.p95 = getJoinedNullDataSet(data[key].views.percentile.p95, {
            fill: 'zero',
            withEnd: true,
            withStart: true,
          });
        } else {
          data[key].views.percentile.p5 = getJoinedNullDataSet(data[key].views.percentile.p5);
          data[key].views.percentile.p50 = getJoinedNullDataSet(data[key].views.percentile.p50);
          data[key].views.percentile.p95 = getJoinedNullDataSet(data[key].views.percentile.p95);
        }
      });

      setPayload({
        isEmpty: Object.keys(data).length === 0,
        metrics: data,
      });
    }
  }, [data, metric]);

  return {
    isError,
    isFetched: isSuccess,
    isFetching,
    refetch,
    ...payload,
  };
}
